import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgModule, CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA } from '@angular/core';
import { DatePipe } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClient } from '@angular/common/http';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { NgIdleKeepaliveModule } from '@ng-idle/keepalive';
import { LottieModule  } from 'ngx-lottie';
import player from 'lottie-web';
import { from, Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import {
  EuCoreServicesModule, EuDataServicesModule, EuFeatureFlagsModule,
  HeaderSimpleModule, FooterSimpleModule, ChatbotLoaderModule,
  BootstrapBreakpointDetectorModule, AlertModule,
  AppInsightsModule
} from '@eucoms/common-components';

import { TranslateService } from '@app/core/services/translate.service';
import { TranslatePipe } from '@app/core/services/translate.pipe';
import { FallbackReportModule } from './fallback-report/fallback-report.module';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { LoaderInterceptor } from './core/interceptors/loader-interceptor';
import { AuthenticationService } from './core/services/authentication.service';
import { LoaderService } from './core/services/loader.service';
import { SharedService } from './core/services/shared.service';
import { GoogleAnalyticsService } from './core/services/google-analytics-service';
import { SharedModule } from './shared/shared.module';
import { environment } from '@environments/environment';
import { coreServicesInitOptions } from '@config/app-config';
import { constants } from './constants';
import { appFeatureFlags } from './app-feature-flag';
import { MockBaseUrlFeatureFlagService } from './tracker/configurations/mock-baseurl-featureflag.service';
import { TimeFormatService } from '@app/tracker/services/time-format.service';
import { GlobalBannerHelperService } from './core/services/global-banner-helper.service';
import { ApiLoggingService } from './core/services/api-logging.service';
import { EudsFooterFullModule, EudsOpcoBrandingModule } from '@eucoms/common-components/design-system';
import { RecaptchaModule } from '@eucoms/common-components/recaptcha-v3';

export function featureFlagsFactory () {
  if (environment.isHeadless) {
    return [EuFeatureFlagsModule.mockFeatureFlagService({ ...constants.featureFlags }, appFeatureFlags)];
  }
  if (environment.devEnvironment && environment.isJourney) {
    return [MockBaseUrlFeatureFlagService.forRoot()];
  }
  return [];
}
export function playerFactory() {
  return player;
}

export class LazyTranslateLoader implements TranslateLoader {
  getTranslation(lang: string): Observable<any> {
    return from(import(`../assets/i18n/${lang}.json`)).pipe(catchError(err => {
      console.warn(`error fetching ${lang} translation file`);
      return of({});
    }));
  }
}

// temp for Retrieval Credentials POC - disable Alerts
@NgModule({
  declarations: [
    AppComponent,
    TranslatePipe
    // NgLottieComponent,
  ],
  imports: [
    RecaptchaModule,
    EuCoreServicesModule.forRoot(coreServicesInitOptions),
    EuFeatureFlagsModule.forRoot({ ...constants.featureFlags, ...appFeatureFlags }),
    EuDataServicesModule,
    LottieModule.forRoot({ player: playerFactory }),
    NgIdleKeepaliveModule.forRoot(),
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    SharedModule,
    HeaderSimpleModule,
    FooterSimpleModule,
    FallbackReportModule,
    ChatbotLoaderModule,
    BootstrapBreakpointDetectorModule,
    AlertModule,
    EudsOpcoBrandingModule,
    EudsFooterFullModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useClass: (LazyTranslateLoader)
      }
    }),
    AppInsightsModule
  ],
  bootstrap: [AppComponent],
  providers: [
    ...featureFlagsFactory(),
    GlobalBannerHelperService,
    TranslateService,
    AuthenticationService,
    LoaderService,
    SharedService,
    GoogleAnalyticsService,
    TimeFormatService,
    DatePipe,
    { provide: HTTP_INTERCEPTORS, useClass: LoaderInterceptor, multi: true },
    { provide: 'windowObject', useValue: window},
    ApiLoggingService
  ],
  schemas: [ CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA ]
})
export class AppModule { }

/**
 * The HttpLoaderFactory function returns a Object that can load Translations using `Http` and `.json`
*/
export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}
